import { useEffect, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { Content, Dialog, DialogContainer, Flex, ProgressCircle } from '@adobe/react-spectrum';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { GetProjectRequest } from '../../../../services/soap/project/requests/GetProjectRequest';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import FolderComponent from './FolderComponent';
import ProjectComponent from './ProjectComponent';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import ActivityComponent from './ActivityComponent';
import FolderComponentMobile from './FolderComponentMobile';
import ProjectComponentMobile from './ProjectComponentMobile';
import ActivityComponentMobile from './ActivityComponentMobile';
import { MenuItemModel } from '@syncfusion/ej2-react-navigations';
import PrintDialog from './component/PrintDialog/PrintDialog';
import { invokeCreateContextApp } from '../../../../utils/NavigationUtils';
import { ListMessageContainerNotificationRequest } from '../../../../services/soap/message/requests/ListMessageContainerNotificationRequest';
import { Filter } from '../../../../services/soap/dataObjects/Filter';
import { publish, AtollonEvent } from '../../../../infra/events/Events';
import { CreateMessageContainerNotificationRequest } from '../../../../services/soap/message/requests/CreateMessageContainerNotificationRequest';
import { DeleteMessageContainerNotificationRequest } from '../../../../services/soap/message/requests/DeleteMessageContainerNotificationRequest';

export interface IHeaderToolbarProps {
  readonly onPrint?: (item: FPAData) => void;
  readonly onEdit?: (item: FPAData) => void;
  readonly onDelete?: (item: FPAData) => void;
  readonly onAI?: (item: FPAData) => void;
  readonly selectedItem?: FPAData;
}

function HeaderToolbar({ onPrint, onEdit, onDelete, onAI, selectedItem }: IHeaderToolbarProps) {
  const { store, projectService, messageService } = useDependency();
  const { isMobile } = useViewInfo();
  const [headerData, setHeaderData] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [isPrintOpen, setIsPrintOpen] = useState<boolean>(false);
  const [teamInboxRowId, setTeamInboxRowId] = useState<string | null>(null);
  const [isTeamInbox, setIsTeamInbox] = useState<boolean>(false);
  const { t } = useTranslation();

  const copy_item = {
    iconCss: 'bi bi-copy',
    text: t('copy', { ns: 'layout_components' }),
    id: 'copy',
  };

  const move_item = {
    iconCss: 'bi bi-forward-fill',
    text: t('move', { ns: 'layout_components' }),
    id: 'move',
  };

  const team_inbox_add_item = {
    iconCss: 'bi bi-inbox-fill',
    text: t('mark_as_team_inbox', {
      ns: 'layout_components',
      defaultValue: 'Mark as Team Inbox',
    }),
    id: 'teamInboxAdd',
  };

  const team_inbox_remove_item = {
    iconCss: 'bi bi-inbox',
    text: t('remove_team_inbox', {
      ns: 'layout_components',
      defaultValue: 'Remove Team Inbox',
    }),
    id: 'teamInboxRemove',
  };

  let menuItems: MenuItemModel[] = [
    {
      iconCss: 'bi bi-printer-fill',
      text: t('print', { ns: 'layout_components' }),
      id: 'print',
    },
    {
      iconCss: 'bi bi-pencil-square',
      text: t('edit', { ns: 'layout_components' }),
      id: 'edit',
    },
    {
      iconCss: 'bi bi-trash',
      text: t('delete', { ns: 'layout_components' }),
      id: 'delete',
    },
  ];

  if (selectedItem?.type === FPADataTypes.FOLDER) {
    menuItems.push(isTeamInbox ? team_inbox_remove_item : team_inbox_add_item);
  }

  if (selectedItem) {
    switch (selectedItem.type) {
      case FPADataTypes.PROJECT:
      case FPADataTypes.ACTIVITY:
        menuItems.push(move_item, copy_item);
        break;
      default:
        break;
    }
  }
  useEffect(() => {
    //console.log('selectedItem.type', selectedItem?.type);
    (async () => {
      //console.log('selectedItem', selectedItem);
      setShowLoader(true);
      setIsTeamInbox(false);
      setTeamInboxRowId(null);
      if (selectedItem?.type === FPADataTypes.FOLDER) {
        const response = await projectService.getFolder(new GetFolderRequest(store.Server, store.SessionId, selectedItem?.id || 0));
        setHeaderData(response.FOLDER);
        //console.log('response.FOLDER', response.FOLDER);
        try {
          const userId = String(store.UserId || '').trim();
          const folder = response.FOLDER;
          const msgDefault = String(folder?.messageDefault ?? '').trim();
          if (userId) {
            const FILTER: Filter = {
              COL: [{ name: 'MessageRecipient', type: 'equals', value: userId }],
            };
            const res = await messageService.listMessageContainerNotification(
              new ListMessageContainerNotificationRequest(store.Server, store.SessionId, {
                limit: '200',
                offset: '0',
                FILTER,
              }),
            );
            const rows = Array.isArray((res as any)?.ROW) ? (res as any).ROW : [];
            const matched = rows.find((r: any) => {
              const name = String(r?.MessageContainerName ?? '');
              if (name === 'My Messages') return false;
              const container = String(r?.MessageContainer ?? '').trim();
              return !!msgDefault && container === msgDefault;
            });
            if (matched?.id) {
              setIsTeamInbox(true);
              setTeamInboxRowId(String(matched.id));
            }
          }
        } catch (e) {
          // Ignore lookup errors - the menu item still shows add/remove based on current state.
          console.error('Team inbox status lookup failed', e);
        }
        setShowLoader(false);
      } else if (selectedItem?.type === FPADataTypes.PROJECT) {
        const response = await projectService.getProject(new GetProjectRequest(store.Server, store.SessionId, selectedItem?.id));
        setHeaderData(response.PROJECT);
        //console.log('response.PROJECT', response.PROJECT);
        setShowLoader(false);
      } else if (selectedItem?.type === FPADataTypes.ACTIVITY) {
        const response = await projectService.getActivity(new GetActivityRequest(store.Server, store.SessionId, selectedItem?.id));
        setHeaderData(response.ACTIVITY);
        //console.log('response.Activity', response);
        if (response.result === 'FAIL') {
          ToastQueue.negative(response.EXCEPTION.message, { timeout: 1000 });
        }
        setShowLoader(false);
      }
    })();
    //console.log('selectedItem in header', selectedItem);
  }, [messageService, projectService, selectedItem?.id, store.Server, store.SessionId, store.UserId]);

  function statusColor(color: string) {
    //console.log('customStateID', customStateID);
    if (!color || color === '0') return '';
    try {
      let finalColor = (+color).toString(16).split('').reverse();
      let padding = Array(6 - finalColor?.length).fill('0');
      let finalColorString = '#' + [...finalColor, ...padding].join('');
      //console.log('finalColorString', finalColorString);
      return finalColorString;
    } catch (error) {
      return '';
    }
  }

  const handleMenuItemClick = (args: any) => {
    //console.log('--------- handleMenuItemClick mobile: ', args?.item);
    switch (args?.item?.id) {
      case 'print':
        console.log('Print clicked');
        onPrint && selectedItem && onPrint(selectedItem);
        setIsPrintOpen(true);
        break;
      case 'edit':
        console.log('Edit clicked');
        onEdit && selectedItem && onEdit(selectedItem);
        break;
      case 'delete':
        console.log('Delete clicked');
        onDelete && selectedItem && onDelete(selectedItem);
        break;
      case 'teamInboxAdd':
        (async () => {
          if (selectedItem?.type !== FPADataTypes.FOLDER) return;
          const folder = headerData;
          const userId = String(store.UserId || '').trim();
          const msgDefault = String(folder?.messageDefault ?? '').trim();
          const msgGroup = String(folder?.messageGroup ?? '').trim();
          const msgPath = String(folder?.messageDefaultPath ?? '').trim();
          const name = String(folder?.name ?? selectedItem?.title ?? '').trim();
          if (!userId || !msgDefault || !msgGroup) {
            ToastQueue.negative(
              t('team_inbox_missing_container', {
                ns: 'layout_components',
                defaultValue:
                  'This folder cannot be configured as Team Inbox (missing message container data).',
              }),
              { timeout: 2500 },
            );
            return;
          }
          const res: any = await messageService.createMessageContainerNotification(
            new CreateMessageContainerNotificationRequest(store.Server, store.SessionId, {
              MessageRecipient: userId,
              MessageContainerName: name,
              MessageContainerPath: msgPath,
              MessageContainerGroup: msgGroup,
              MessageContainer: msgDefault,
            }),
          );
          if (res?.result !== 'OK') {
            ToastQueue.negative(
              res?.EXCEPTION?.message ||
                t('team_inbox_failed', {
                  ns: 'layout_components',
                  defaultValue: 'Failed to set Team Inbox',
                }),
              { timeout: 2500 },
            );
            return;
          }
          setIsTeamInbox(true);
          try {
            const FILTER: Filter = {
              COL: [{ name: 'MessageRecipient', type: 'equals', value: userId }],
            };
            const listRes = await messageService.listMessageContainerNotification(
              new ListMessageContainerNotificationRequest(store.Server, store.SessionId, {
                limit: '200',
                offset: '0',
                FILTER,
              }),
            );
            const rows = Array.isArray((listRes as any)?.ROW) ? (listRes as any).ROW : [];
            const matched = rows.find((r: any) => {
              const container = String(r?.MessageContainer ?? '').trim();
              return container === msgDefault;
            });
            if (matched?.id) {
              setTeamInboxRowId(String(matched.id));
            }
          } catch {
            // ignore
          }
          publish(AtollonEvent.TEAM_INBOX_UPDATED);
          ToastQueue.positive(
            t('team_inbox_set', {
              ns: 'layout_components',
              defaultValue: 'Team Inbox configured',
            }),
            { timeout: 1500 },
          );
        })();
        break;
      case 'teamInboxRemove':
        (async () => {
          const id = String(teamInboxRowId ?? '').trim();
          if (!id) return;
          const res: any = await messageService.deleteMessageContainerNotification(
            new DeleteMessageContainerNotificationRequest(store.Server, store.SessionId, id),
          );
          if (res?.result !== 'OK') {
            ToastQueue.negative(
              res?.EXCEPTION?.message ||
                t('team_inbox_failed_remove', {
                  ns: 'layout_components',
                  defaultValue: 'Failed to remove Team Inbox',
                }),
              { timeout: 2500 },
            );
            return;
          }
          setIsTeamInbox(false);
          setTeamInboxRowId(null);
          publish(AtollonEvent.TEAM_INBOX_UPDATED);
          ToastQueue.positive(
            t('team_inbox_removed', {
              ns: 'layout_components',
              defaultValue: 'Team Inbox removed',
            }),
            { timeout: 1500 },
          );
        })();
        break;
      case 'copy':
        console.log('Copy clicked');
        handleCopyClicked();
        break;
      case 'move':
        console.log('Move clicked');
        handleMoveClicked();
        break;
    }
  };

  const handleCopyClicked = (): void => {
    invokeCreateContextApp(selectedItem?.id?.toString() || '', selectedItem?.type, true, selectedItem);
  };

  const handleMoveClicked = (): void => {
    if (selectedItem?.type === FPADataTypes.ACTIVITY || selectedItem?.type === FPADataTypes.PROJECT) {
      invokeCreateContextApp(selectedItem?.id?.toString() || '', selectedItem?.type, false, selectedItem);
    }
  };

  if (isMobile) {
    //console.log('selectedItem?.type', selectedItem?.type);

    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={'center'} marginTop={10}>
          <ProgressCircle aria-label="Loading…" isIndeterminate />
        </Flex>
      );
    } else if (selectedItem?.type === FPADataTypes.FOLDER) {
      return (
        <>
          <FolderComponentMobile headerData={headerData} statusColor={statusColor} menuItems={menuItems} handleMenuItemClick={handleMenuItemClick} onAIClicked={onAI} selectedItem={selectedItem} />
          <DialogContainer
            isDismissable
            onDismiss={() => {
              setIsPrintOpen(false);
            }}
          >
            {isPrintOpen && (
              <Dialog size="L">
                <Content>
                  <PrintDialog selectedItem={selectedItem} print_type={selectedItem?.type} />
                </Content>
              </Dialog>
            )}
          </DialogContainer>
        </>
      );
    } else if (selectedItem?.type === FPADataTypes.PROJECT) {
      return (
        <>
          <ProjectComponentMobile
            headerData={headerData}
            statusColor={statusColor}
            menuItems={menuItems}
            handleMenuItemClick={handleMenuItemClick}
            onAI={onAI}
            selectedItem={selectedItem}
            onCopy={handleCopyClicked}
            onMove={handleMoveClicked}
          />

          <DialogContainer
            isDismissable
            onDismiss={() => {
              setIsPrintOpen(false);
            }}
          >
            {isPrintOpen && (
              <Dialog size="L">
                <Content>
                  <PrintDialog selectedItem={selectedItem} print_type={selectedItem?.type} />
                </Content>
              </Dialog>
            )}
          </DialogContainer>
        </>
      );
    } else if (selectedItem?.type === FPADataTypes.ACTIVITY) {
      return (
        <>
          <ActivityComponentMobile
            headerData={headerData}
            statusColor={statusColor}
            menuItems={menuItems}
            handleMenuItemClick={handleMenuItemClick}
            onAI={onAI}
            selectedItem={selectedItem}
            onCopy={handleCopyClicked}
            onMove={handleMoveClicked}
          />
          <DialogContainer
            isDismissable
            onDismiss={() => {
              setIsPrintOpen(false);
            }}
          >
            {isPrintOpen && (
              <Dialog size="L">
                <Content>
                  <PrintDialog selectedItem={selectedItem} print_type={selectedItem?.type} />
                </Content>
              </Dialog>
            )}
          </DialogContainer>
        </>
      );
    } else {
      return '';
    }
  } else {
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={'center'} marginTop={10}>
          <ProgressCircle aria-label="Loading…" isIndeterminate />
        </Flex>
      );
    } else if (selectedItem?.type === FPADataTypes.FOLDER) {
      return <FolderComponent headerData={headerData} statusColor={statusColor} onPrint={onPrint} onEdit={onEdit} onDelete={onDelete} selectedItem={selectedItem} />;
    } else if (selectedItem?.type === FPADataTypes.PROJECT) {
      return (
        <ProjectComponent
          headerData={headerData}
          statusColor={statusColor}
          onPrint={onPrint}
          onEdit={onEdit}
          onDelete={onDelete}
          onAI={onAI}
          onCopy={handleCopyClicked}
          onMove={handleMoveClicked}
          selectedItem={selectedItem}
        />
      );
    } else if (selectedItem?.type === FPADataTypes.ACTIVITY) {
      return (
        <ActivityComponent
          headerData={headerData}
          statusColor={statusColor}
          onPrint={onPrint}
          onEdit={onEdit}
          onDelete={onDelete}
          onCopy={handleCopyClicked}
          onMove={handleMoveClicked}
          selectedItem={selectedItem}
        />
      );
    } else {
      return '';
    }
  }
}

export const HeaderToolbarComponent = LayoutComponent(HeaderToolbar);
